// Reasoning — the Architect's construction-based reasoning primitives
// rendered as a single public workbench:
//   1. Load-path analysis — score any text; see which sentences carry
//      structural work and which are ornament.
//   2. Refusal-with-reason — generate warm, reasoned refusals in the
//      contractor-explaining-code voice for any of six categories.
//   3. Structural-improvement diff — compare a before/after and see
//      which load-bearing claims survived, were added, or were removed.
//   4. Regenerative synthesis — the phone workflow: verify base facts,
//      identify depleting inputs, propose abundant/renewable swaps
//      from the curated knowledge base.
//
// No auth required. Everything runs against /api/reasoning/*.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  Hammer,
  ShieldCheck,
  GitCompare,
  Recycle,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Anchor,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Colour a sentence card by its load-path tag.
const TAG_STYLE = {
  load_bearing: "border-emerald-500/50 bg-emerald-500/[0.06] text-emerald-100",
  mixed: "border-amber-500/40 bg-amber-500/[0.04] text-amber-100",
  ornamental: "border-zinc-800 bg-[#0b0b0d] text-zinc-500",
};
const TAG_LABEL = {
  load_bearing: "load-bearing",
  mixed: "mixed",
  ornamental: "ornament",
};

// Verdict pill colours for the load-path report.
const VERDICT_STYLE = {
  solid: "text-emerald-300 border-emerald-500/40",
  workable: "text-amber-300 border-amber-500/40",
  ornament_heavy: "text-fuchsia-300 border-fuchsia-500/40",
  empty: "text-zinc-400 border-zinc-800",
  improved: "text-emerald-300 border-emerald-500/40",
  regressed: "text-red-300 border-red-500/40",
  ornament_only: "text-fuchsia-300 border-fuchsia-500/40",
  neutral: "text-zinc-300 border-zinc-700",
};

function Pill({ verdict, testid }) {
  const style = VERDICT_STYLE[verdict] || VERDICT_STYLE.neutral;
  return (
    <span
      data-testid={testid}
      className={`inline-block font-mono text-[10px] uppercase tracking-[0.25em] border px-2 py-0.5 ${style}`}
    >
      {verdict.replace(/_/g, " ")}
    </span>
  );
}

function SectionShell({ id, icon: Icon, title, blurb, children, testid }) {
  return (
    <section id={id} className="border border-zinc-800 bg-[#0a0a0c] p-6 mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-emerald-300" />
        <h2 className="font-heading text-xl tracking-[0.04em] text-zinc-100">
          {title}
        </h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4 leading-relaxed">{blurb}</p>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Load-path panel
// ---------------------------------------------------------------------------
function LoadPathPanel() {
  const [text, setText] = useState(
    "The Eiffel Tower is 330 meters tall. It was completed in 1889. Gustave Eiffel's firm engineered the wrought-iron lattice. Basically, it's a really cool place."
  );
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/reasoning/load-path`, { text });
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionShell
      id="load-path"
      icon={Hammer}
      title="Load-path analysis"
      blurb="Which sentences carry structural work, which are ornament. Every sentence in a reply is either load-bearing (a claim the answer will collapse without) or ornamental (rhetorical filler that could be trimmed). Paste text and see the read-out."
      testid="reasoning-load-path"
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-3 font-mono leading-relaxed focus:border-emerald-500/60 focus:outline-none"
        data-testid="load-path-input"
        placeholder="Paste any block of text — an AI reply, an email, a policy paragraph…"
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={run}
          disabled={loading || !text.trim()}
          data-testid="load-path-run"
          className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-emerald-200 text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Hammer className="h-3 w-3" />}
          Score
        </button>
        {error ? <span className="text-xs text-red-400" data-testid="load-path-error">{error}</span> : null}
      </div>

      {report ? (
        <div className="mt-6" data-testid="load-path-report">
          <div className="flex items-center gap-3 mb-3">
            <Pill verdict={report.verdict} testid="load-path-verdict" />
            <div className="text-xs text-zinc-400">
              {report.load_bearing_count} load-bearing · {report.mixed_count} mixed · {report.ornamental_count} ornament
              <span className="text-zinc-600"> · ratio {report.load_path_ratio}</span>
            </div>
          </div>
          <p className="text-sm text-zinc-300 mb-4 leading-relaxed" data-testid="load-path-summary">
            {report.summary}
          </p>
          <div className="grid gap-2">
            {report.sentences?.map((s) => (
              <div
                key={s.index}
                className={`border px-3 py-2 text-sm leading-relaxed ${TAG_STYLE[s.tag] || TAG_STYLE.ornamental}`}
                data-testid={`load-path-sentence-${s.index}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-70">
                    {TAG_LABEL[s.tag]}
                  </span>
                  {s.load_signals?.length ? (
                    <span className="font-mono text-[9px] opacity-60">
                      + {s.load_signals.join(", ")}
                    </span>
                  ) : null}
                  {s.ornament_signals?.length ? (
                    <span className="font-mono text-[9px] opacity-60">
                      − {s.ornament_signals.join(", ")}
                    </span>
                  ) : null}
                </div>
                <div>{s.text}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// Refusal-with-reason panel
// ---------------------------------------------------------------------------
function RefusalPanel() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("injection");
  const [topic, setTopic] = useState("attempt to override system prompt");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/reasoning/refusal-categories`)
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([
        "harm", "injection", "identity", "hallucination_risk", "out_of_scope", "unclear",
      ]));
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/reasoning/refuse`, { category, topic });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionShell
      id="refusal"
      icon={ShieldCheck}
      title="Refusal-with-reason"
      blurb="Never cold, always structural. The Sentinel refuses the way a remodel specialist explains code to a client — a warm, reasoned no that leaves the relationship intact. Pick a category and see what the tether actually says."
      testid="reasoning-refusal"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="refusal-category"
            className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-2 focus:border-emerald-500/60 focus:outline-none"
          >
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Topic (audit-only, not echoed)</span>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            data-testid="refusal-topic"
            className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-2 focus:border-emerald-500/60 focus:outline-none"
          />
        </label>
      </div>
      <div className="mt-3">
        <button
          onClick={run}
          disabled={loading}
          data-testid="refusal-run"
          className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-40 text-emerald-200 text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
          Generate refusal
        </button>
      </div>
      {result ? (
        <div className="mt-6 border-l-2 border-emerald-500/60 bg-[#08080a] p-4" data-testid="refusal-result">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">
            {result.category} · {result.tone.replace(/_/g, " ")}
          </div>
          <p className="text-zinc-200 text-sm leading-relaxed">{result.refusal}</p>
        </div>
      ) : null}
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// Structural-improvement diff panel
// ---------------------------------------------------------------------------
function ImprovementPanel() {
  const [before, setBefore] = useState(
    "Basically, it depends. Maybe it could work."
  );
  const [after, setAfter] = useState(
    "The reactor produces 1,200 megawatts of continuous output. It requires 3,500 tons of cooling water per hour."
  );
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/reasoning/evaluate-improvement`, { before, after });
      setDiff(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionShell
      id="improvement"
      icon={GitCompare}
      title="Structural improvement diff"
      blurb="Did a rewrite actually improve the structure, or just add ornament? Compare before/after and see which load-bearing claims survived, which were added, and which were removed."
      testid="reasoning-improvement"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Before</span>
          <textarea
            value={before}
            onChange={(e) => setBefore(e.target.value)}
            rows={5}
            data-testid="improvement-before"
            className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-3 font-mono leading-relaxed focus:border-emerald-500/60 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">After</span>
          <textarea
            value={after}
            onChange={(e) => setAfter(e.target.value)}
            rows={5}
            data-testid="improvement-after"
            className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-3 font-mono leading-relaxed focus:border-emerald-500/60 focus:outline-none"
          />
        </label>
      </div>
      <div className="mt-3">
        <button
          onClick={run}
          disabled={loading}
          data-testid="improvement-run"
          className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-40 text-emerald-200 text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitCompare className="h-3 w-3" />}
          Diff
        </button>
      </div>
      {diff ? (
        <div className="mt-6" data-testid="improvement-result">
          <div className="flex items-center gap-3 mb-4">
            <Pill verdict={diff.verdict} testid="improvement-verdict" />
            <div className="text-xs text-zinc-500">
              Δ load-path ratio: <span className="font-mono text-zinc-300">{diff.delta_ratio > 0 ? "+" : ""}{diff.delta_ratio}</span>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="border border-emerald-500/30 bg-emerald-500/[0.04] p-3" data-testid="improvement-added">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">
                Added ({diff.load_added?.length || 0})
              </div>
              {diff.load_added?.length ? diff.load_added.map((c, i) => (
                <div key={i} className="text-xs text-zinc-300 leading-relaxed mb-2">{c}</div>
              )) : <div className="text-xs text-zinc-600">— none —</div>}
            </div>
            <div className="border border-zinc-700 bg-[#0b0b0d] p-3" data-testid="improvement-preserved">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
                Preserved ({diff.load_preserved?.length || 0})
              </div>
              {diff.load_preserved?.length ? diff.load_preserved.map((c, i) => (
                <div key={i} className="text-xs text-zinc-300 leading-relaxed mb-2">{c}</div>
              )) : <div className="text-xs text-zinc-600">— none —</div>}
            </div>
            <div className="border border-red-500/30 bg-red-500/[0.04] p-3" data-testid="improvement-removed">
              <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 mb-2">
                Removed ({diff.load_removed?.length || 0})
              </div>
              {diff.load_removed?.length ? diff.load_removed.map((c, i) => (
                <div key={i} className="text-xs text-zinc-300 leading-relaxed mb-2">{c}</div>
              )) : <div className="text-xs text-zinc-600">— none —</div>}
            </div>
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// Regenerative synthesis panel — the gift.
// ---------------------------------------------------------------------------

// Domain groupings for the "KB knows" display. Live domains have real
// resources in the backend KB; coming_soon domains are UI placeholders
// that pre-build the tab structure so new domain modules slot into a
// ready home rather than requiring UI work each time one lands.
// When a new resource gets added to the backend KB, add it to the
// matching resources[] array here and it appears in the correct tab.
const REGEN_DOMAINS = [
  {
    id: "construction",
    label: "Construction",
    status: "live",
    resources: ["cement", "sand", "steel rebar", "wood", "gypsum", "asphalt"],
    note: null,
  },
  {
    id: "soil",
    label: "Soil & Agriculture",
    status: "live",
    resources: ["topsoil", "synthetic fertilizer", "phosphorus", "nitrogen fertilizer"],
    note: null,
  },
  {
    id: "water",
    label: "Water",
    status: "live",
    resources: ["freshwater", "chlorine water treatment", "aquifer depletion"],
    note: null,
  },
  {
    id: "food",
    label: "Food & Nutrition",
    status: "live",
    resources: ["monoculture grain"],
    note: null,
  },
  {
    id: "materials",
    label: "Energy & Materials",
    status: "live",
    resources: ["lithium", "cobalt", "copper", "rare earth magnets", "helium", "oil"],
    note: null,
  },
  {
    id: "health",
    label: "Health & Medicine",
    status: "coming_soon",
    resources: [],
    note: "Ethnopharmacology + traditional health practices KB — in curation",
  },
  {
    id: "governance",
    label: "Governance & Restorative",
    status: "coming_soon",
    resources: [],
    note: "Consensus circles · restorative justice · rotating councils — in curation",
  },
];

function RegenerativeDomainTabs({ knownResources, onResourceClick }) {
  const [activeId, setActiveId] = useState("construction");
  const active = REGEN_DOMAINS.find((d) => d.id === activeId) || REGEN_DOMAINS[0];
  // Only surface resources that are BOTH declared in the domain map AND
  // actually returned by the backend — protects against drift if a
  // resource is removed from the KB but not from the frontend map.
  const liveResources = active.resources.filter((r) => knownResources.includes(r));

  return (
    <div data-testid="regen-domain-tabs">
      <div className="flex flex-wrap gap-1 mb-3 border-b border-zinc-900">
        {REGEN_DOMAINS.map((d) => {
          const isActive = d.id === activeId;
          const isPlaceholder = d.status === "coming_soon";
          return (
            <button
              key={d.id}
              onClick={() => setActiveId(d.id)}
              data-testid={`regen-domain-tab-${d.id}`}
              className={`text-[10px] uppercase tracking-[0.25em] px-3 py-2 border-b-2 transition-colors ${
                isActive
                  ? isPlaceholder
                    ? "border-amber-400 text-amber-300"
                    : "border-emerald-400 text-emerald-300"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {d.label}
              {isPlaceholder ? (
                <span className="ml-1.5 text-[8px] text-amber-500/60">◦ soon</span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="min-h-[42px]">
        {active.status === "coming_soon" ? (
          <div
            className="text-[11px] font-mono text-amber-300/70 border border-amber-500/20 bg-amber-500/5 px-3 py-2"
            data-testid={`regen-domain-placeholder-${active.id}`}
          >
            <span className="uppercase tracking-[0.3em] text-amber-400/80 mr-2">Coming</span>
            {active.note}
          </div>
        ) : liveResources.length ? (
          <div className="flex flex-wrap gap-1.5">
            {liveResources.map((r) => (
              <button
                key={r}
                onClick={() => onResourceClick?.(r)}
                data-testid={`regen-domain-resource-${r.replace(/\s+/g, "-")}`}
                className="text-[10px] font-mono text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10 border border-zinc-800 hover:border-emerald-500/40 px-2 py-1 transition-colors"
                title={`Click to add "${r}" to depleting inputs`}
              >
                {r}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-[11px] font-mono text-zinc-600">
            No live resources in this domain yet — coming soon.
          </div>
        )}
      </div>
    </div>
  );
}

function RegenerativePanel() {
  const [topic, setTopic] = useState("residential slab foundation");
  const [factsText, setFactsText] = useState(
    "Portland cement production emits ~8% of global CO2.\nRiver sand is being extracted faster than natural replenishment."
  );
  const [inputsText, setInputsText] = useState("Portland cement, river sand");
  const [knownResources, setKnownResources] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/reasoning/known-depleting-resources`)
      .then((r) => setKnownResources(r.data.resources || []))
      .catch(() => {});
  }, []);

  const facts = useMemo(
    () => factsText.split("\n").map((l) => l.trim()).filter(Boolean),
    [factsText]
  );
  const inputs = useMemo(
    () => inputsText.split(",").map((l) => l.trim()).filter(Boolean),
    [inputsText]
  );

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/reasoning/regenerate`, {
        topic,
        verified_facts: facts,
        depleting_inputs: inputs,
      });
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionShell
      id="regenerate"
      icon={Recycle}
      title="Regenerative synthesis"
      blurb="The Architect's phone workflow, wired native. Start with verified facts about a current structure, name which resources it depends on that are running out, and the tether proposes abundant/renewable swaps from the curated knowledge base — each with why it works and which depleting resource it relieves."
      testid="reasoning-regenerate"
    >
      <label className="block mb-3">
        <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Topic — what structure?</span>
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          data-testid="regenerate-topic"
          className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-2 focus:border-emerald-500/60 focus:outline-none"
        />
      </label>
      <label className="block mb-3">
        <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Verified facts (one per line)</span>
        <textarea
          value={factsText}
          onChange={(e) => setFactsText(e.target.value)}
          rows={4}
          data-testid="regenerate-facts"
          className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-3 font-mono leading-relaxed focus:border-emerald-500/60 focus:outline-none"
        />
      </label>
      <label className="block mb-3">
        <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Depleting inputs (comma-separated)</span>
        <input
          value={inputsText}
          onChange={(e) => setInputsText(e.target.value)}
          data-testid="regenerate-inputs"
          className="w-full bg-[#08080a] border border-zinc-800 text-zinc-200 text-sm p-2 focus:border-emerald-500/60 focus:outline-none"
        />
      </label>
      {knownResources.length ? (
        <div className="mb-4" data-testid="regenerate-kb">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
            KB knows — by domain
          </div>
          <RegenerativeDomainTabs
            knownResources={knownResources}
            onResourceClick={(r) => {
              const cur = inputs;
              if (!cur.includes(r)) {
                setInputsText(cur.length ? `${inputsText}, ${r}` : r);
              }
            }}
          />
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <button
          onClick={run}
          disabled={loading || !topic.trim()}
          data-testid="regenerate-run"
          className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-emerald-200 text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          Propose swaps
        </button>
        {error ? <span className="text-xs text-red-400" data-testid="regenerate-error">{error}</span> : null}
      </div>
      {report ? (
        <div className="mt-6" data-testid="regenerate-result">
          <p className="text-sm text-zinc-300 mb-4 leading-relaxed">{report.structural_summary}</p>

          {report.load_path_of_input_facts ? (
            <div className="border border-zinc-800 bg-[#08080a] p-3 mb-6 text-xs text-zinc-400" data-testid="regenerate-lp">
              <span className="uppercase tracking-[0.3em] text-zinc-500 mr-2">Input facts scored:</span>
              {report.load_path_of_input_facts.summary}
            </div>
          ) : null}

          {report.unresolved_inputs?.length ? (
            <div className="border border-amber-500/30 bg-amber-500/[0.05] p-3 mb-6" data-testid="regenerate-unresolved">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-3 w-3 text-amber-300" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
                  Unresolved — needs human research
                </span>
              </div>
              <p className="text-xs text-zinc-400 mb-2">
                The knowledge base doesn&apos;t cover these yet. The honest thing is to flag them rather than fabricate substitutes:
              </p>
              <ul className="list-disc list-inside text-xs text-zinc-300">
                {report.unresolved_inputs.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="grid gap-4">
            {report.proposals?.map((p) => (
              <div key={p.canonical} className="border border-emerald-500/30 bg-emerald-500/[0.03] p-4" data-testid={`regenerate-proposal-${p.canonical}`}>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-3 w-3 text-emerald-300" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">
                    Depleting input · {p.depleting_input}
                    {p.depleting_input.toLowerCase() !== p.canonical ? (
                      <span className="text-zinc-500"> → {p.canonical}</span>
                    ) : null}
                  </span>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {p.candidates?.map((c, i) => (
                    <div key={i} className="border border-zinc-800 bg-[#0b0b0d] p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-heading text-sm text-zinc-100 tracking-[0.02em]">
                          {c.name}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                          {c.domain}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-400 leading-relaxed mb-2">{c.why}</p>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">
                        Relieves: <span className="text-emerald-200 normal-case tracking-normal">{c.relieves}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function Reasoning() {
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="reasoning-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="border-b border-zinc-900 pb-8 mb-10"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-3">
            <Anchor className="h-3 w-3" /> Reasoning
          </div>
          <h1
            data-testid="reasoning-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[0.04em] leading-[1.05]"
          >
            The Architect&apos;s reasoning, wired native.
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg mt-6 max-w-3xl leading-relaxed">
            Four primitives — load-path analysis, refusal-with-reason,
            structural improvement diff, and regenerative synthesis —
            that let anyone walk the same reasoning path a load-bearing
            engineer walks before he swings the hammer. The last one is
            the gift: verify what&apos;s true, name what&apos;s running
            out, propose what could replace it, and rebuild toward
            structures the world can actually regenerate.
          </p>
        </motion.header>

        <LoadPathPanel />
        <RefusalPanel />
        <ImprovementPanel />
        <RegenerativePanel />

        <div className="border-t border-zinc-900 pt-8 mt-10 text-xs text-zinc-500 leading-relaxed">
          Everything on this page is deterministic — no LLM call, no
          hallucination surface. The reasoning engine ships as{" "}
          <code className="text-emerald-300">backend/construction_reasoning.py</code>{" "}
          and is exposed at <code>/api/reasoning/*</code>. See{" "}
          <Link to="/origin" className="text-emerald-300 hover:text-emerald-100">
            /origin
          </Link>{" "}
          for why this reasoning exists at all, and{" "}
          <Link to="/how-it-works" className="text-emerald-300 hover:text-emerald-100">
            /how-it-works
          </Link>{" "}
          for how the tether uses it upstream of every reply.
        </div>
      </div>
    </div>
  );
}
